import { useEffect, useState } from "react";
import { Check, History, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, pollJob } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { AssetThumb } from "@/components/AssetThumb";

export const EDITABLE = ["idea", "draft", "changes_requested", "rejected"];
const IDEAS = ["Make it shorter and more direct", "Lead with the customer's problem", "Use a more formal tone", "Stronger call to action"];

export function RewritePanel({ item, onShowVersions }) {
  const { path, refresh } = useWorkspace();
  const [guidance, setGuidance] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(path(`/content/${item.id}/rewrite`), { guidance });
      await pollJob(path(`/jobs/${data.job_id}`));
      toast.success("Draft rewritten. The previous version is saved in history.");
      setGuidance(""); refresh();
    } catch (e) { toast.error(e.response ? apiError(e) : e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="draft-rewrite-panel" className="p-4 rounded-lg border border-[#855BFF]/30 bg-[#855BFF]/5">
      <div className="flex items-center justify-between mb-2"><div className="cq-muted-label">Rewrite with guidance</div>
        <button data-testid="draft-show-history" onClick={() => onShowVersions(item)} className="text-xs text-[#AAB6CC] hover:text-[#22E6F2] inline-flex items-center gap-1"><History size={12} /> History (v{item.version || 1})</button></div>
      <div className="flex flex-wrap gap-1.5 mb-2">{IDEAS.map((t) => <button key={t} type="button" disabled={busy} onClick={() => setGuidance(t)} className="px-2.5 py-1 rounded-md text-[11px] border border-[#243153] text-[#AAB6CC] hover:text-[#F7FAFF]">{t}</button>)}</div>
      <textarea data-testid="draft-rewrite-guidance" rows={2} disabled={busy} className="cq-input" placeholder="e.g. Speak to Ontario owner-operators and mention the free Health Check" value={guidance} onChange={(e) => setGuidance(e.target.value)} />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[11px] text-[#7C8BAD]">Uses your Brand Brain and approved assets. Stays a draft until approved.</span>
        <button data-testid="draft-rewrite-submit" disabled={busy || guidance.trim().length < 3} onClick={run} className="cq-btn cq-btn-primary !py-1.5 !text-xs shrink-0">
          {busy ? <><Loader2 size={13} className="animate-spin" /> Rewriting…</> : <><Wand2 size={13} /> Rewrite draft</>}</button>
      </div>
    </div>
  );
}

export function AssetPicker({ item }) {
  const { path, refresh } = useWorkspace();
  const q = useWsQuery("/r/brand_assets");
  const [sel, setSel] = useState(item.asset_ids || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => setSel(item.asset_ids || []), [item.asset_ids]);
  const approved = (q.data || []).filter((a) => a.approved);
  const dirty = JSON.stringify([...sel].sort()) !== JSON.stringify([...(item.asset_ids || [])].sort());
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 6 ? s : [...s, id]));
  const save = async () => {
    setBusy(true);
    try { await api.put(path(`/content/${item.id}/assets`), { asset_ids: sel }); toast.success("Assets updated"); refresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="draft-asset-picker">
      <div className="flex items-center justify-between mb-2"><div className="cq-muted-label">Approved assets ({sel.length}/6 attached)</div>
        {dirty && <button data-testid="draft-assets-save" disabled={busy} onClick={save} className="cq-btn cq-btn-primary !py-1 !text-xs">{busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save assets</button>}</div>
      {!approved.length ? <p className="text-xs text-[#7C8BAD]">No approved assets yet. Approve assets in the Asset Library to attach them.</p> : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">{approved.map((a) => { const on = sel.includes(a.id); return (
          <button key={a.id} type="button" data-testid={`draft-asset-option-${a.id}`} onClick={() => toggle(a.id)}
            className={`relative rounded-lg border overflow-hidden text-left transition-colors ${on ? "border-[#22E6F2]" : "border-[#172342] opacity-70 hover:opacity-100"}`}>
            <AssetThumb asset={a} className="aspect-[4/3]" />
            {on && <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#22E6F2] text-[#050816] flex items-center justify-center"><Check size={12} /></span>}
            <div className="p-1.5 text-[11px] text-[#AAB6CC] truncate">{a.name}</div>
          </button>); })}</div>)}
    </div>
  );
}
